import type { Maybe } from 'graphql/jsutils/Maybe';
import React, { useMemo } from 'react';

import { getCountriesTargetFromAdTargetingSubmission } from '../../../../common/adTargeting';
import { useFieldArray } from '../../../../common/Form/hooks';
import { TargetingPanel } from '../../../../common/Targeting';
import type {
  TargetingPanelAlerts,
  TargetingPanelHeader,
  TargetingPanelTextViewer,
} from '../../../../common/Targeting/TargetingPanel/TargetingPanel';
import type { TargetingTableRowGroup } from '../../../../common/TargetingTable/TargetingTable';
import { normalizeToArray } from '../../../../common/utils';
import type { AdType } from '../../../../configs';
import type { TargetingTermValue, TargetingValue } from '../../../../models';
import { EntityType } from '../../TraffickingPage/hooks/makePageDrawerPlugin';
import { generateTargetingTableData } from '../utils/utils';

export type Props = Readonly<{
  editMode: boolean;
  entityType?: string;
  lineItemCountryIds?: string[];
  lineItemPublishers?: TargetingValue[];
  adType?: Maybe<AdType>;
  allowEditCountryTarget?: boolean;
  /**
   * Optional chrome props that the panel renders above / instead of its
   * dropdowns + table. `DrawerTargetingTable` builds these from the parent
   * line item / ad data and threads them through so the panel can own the
   * `TargetingHeader`, the invalid-publisher / invalid-country alerts, and
   * the text-view toggle.
   */
  header?: TargetingPanelHeader;
  alerts?: TargetingPanelAlerts;
  textViewer?: TargetingPanelTextViewer;
}>;

function DrawerTargetingBody({
  editMode,
  entityType,
  lineItemCountryIds,
  lineItemPublishers,
  adType,
  allowEditCountryTarget,
  header,
  alerts,
  textViewer,
}: Props): React.JSX.Element {
  const isAd = entityType === EntityType.AD;

  const [
    ,
    { value: adTargetingTermValues, error: adTargetingError, initialValue: initialAdTargetingTermValues },
    adListHelpers,
  ] = useFieldArray<TargetingTermValue>('adTargetingTermValues');
  const [
    ,
    { value: lineItemTermValues, error: lineItemTargetingError },
    lineItemListHelpers,
  ] = useFieldArray<TargetingTermValue>('lineItemTargetingTermValues');

  const tableData: TargetingTableRowGroup[] = useMemo(
    (): TargetingTableRowGroup[] => generateTargetingTableData(adTargetingTermValues, lineItemTermValues),
    [adTargetingTermValues, lineItemTermValues]
  );

  const listHelpers = useMemo(() => {
    if (isAd) {
      return adListHelpers;
    }
    return lineItemListHelpers;
  }, [isAd, adListHelpers, lineItemListHelpers]);

  const countryIdsForFilteringValues = useMemo(() => {
    if (!isAd) {
      return lineItemCountryIds;
    }
    return getCountriesTargetFromAdTargetingSubmission(initialAdTargetingTermValues ?? [], lineItemCountryIds);
  }, [isAd, initialAdTargetingTermValues, lineItemCountryIds]);

  return (
    <TargetingPanel
      data={tableData}
      listHelpers={listHelpers}
      editMode={editMode}
      isAd={isAd}
      isTableToggleable
      editingEntityType={isAd ? 'ad' : 'lineItem'}
      header={header}
      alerts={alerts}
      textViewer={textViewer}
      dropdowns={
        editMode
          ? {
              scope: {
                isAdEntity: isAd,
                lineItemCountryIds,
                countryIdsForFilteringValues,
                lineItemPublishers,
                publishersForFilteringValues: lineItemPublishers,
                adTypes: normalizeToArray(adType),
                allowEditCountryTarget,
              },
              targetings: isAd ? adTargetingTermValues : lineItemTermValues,
              listHelpers,
              validationError: isAd ? adTargetingError : lineItemTargetingError,
            }
          : undefined
      }
      search={{ placeholder: 'Search Targeted Values', className: 'targeting-filter' }}
      emptyState={<div>No current targeting data for this {entityType}.</div>}
    />
  );
}

export default DrawerTargetingBody;
